import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ObservableSet, error } from '~/utils';
import useFunctionRef from './use-function-ref';
function useSelect(options = {}) {
    const { defaultSelected = [], max = Number.MAX_SAFE_INTEGER, min = 0, onChange: onChangeProp } = options;
    const onChange = useFunctionRef(onChangeProp);
    if (min > max)
        error('min cannot be greater than max', 'useSelect');
    if (min < 0)
        error('min cannot be less than 0', 'useSelect');
    const selectedItemsRef = useRef(new ObservableSet(defaultSelected));
    const [values, setValues] = useState(Array.from(selectedItemsRef.current));
    const size = useMemo(() => values.length, [values]);
    const isEmpty = useMemo(() => size === 0, [size]);
    const isFull = useMemo(() => size === max, [size, max]);
    const select = useCallback((item) => {
        if (size >= max)
            return;
        selectedItemsRef.current.add(item);
    }, [max, size]);
    const unselect = useCallback((item) => {
        if (size <= min)
            return;
        selectedItemsRef.current.delete(item);
    }, [min, size]);
    const has = useCallback((item) => selectedItemsRef.current.has(item), []);
    const toggle = useCallback((item) => {
        if (has(item))
            unselect(item);
        else
            select(item);
    }, [select, unselect, has]);
    const reset = useCallback(() => {
        selectedItemsRef.current.clear();
    }, []);
    const selectMany = useCallback((items) => {
        items.forEach((item) => {
            if (size >= max)
                return;
            selectedItemsRef.current.add(item);
        });
    }, [max, size]);
    const deleteMany = useCallback((items) => {
        items.forEach((item) => {
            if (size <= min)
                return;
            selectedItemsRef.current.delete(item);
        });
    }, [min, size]);
    useEffect(() => {
        const unsubscribe = selectedItemsRef.current.subscribe((set) => {
            const newValues = Array.from(set);
            setValues(newValues);
            onChange === null || onChange === void 0 ? void 0 : onChange(newValues);
        });
        return unsubscribe;
    }, [onChange]);
    return {
        select,
        values,
        has,
        unselect,
        reset,
        toggle,
        size,
        isEmpty,
        isFull,
        selectMany,
        deleteMany
    };
}
export function useSingleSelect(options = {}) {
    const { defaultSelected = null, onChange: onChangeProp, required = false } = options;
    const onChange = useFunctionRef(onChangeProp);
    const [selected, setSelected] = useState(defaultSelected);
    const select = useCallback((item) => {
        setSelected(item);
        onChange === null || onChange === void 0 ? void 0 : onChange(item);
    }, [onChange]);
    const unselect = useCallback(() => {
        if (required)
            return;
        setSelected(null);
        onChange === null || onChange === void 0 ? void 0 : onChange(null);
    }, [onChange, required]);
    const toggle = useCallback((item) => {
        if (selected === item)
            unselect();
        else
            select(item);
    }, [selected, select, unselect]);
    return {
        select,
        selected,
        unselect,
        toggle
    };
}
export default useSelect;
//# sourceMappingURL=use-select.js.map